import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Mail, MessageCircle, Share2, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';

/** Editable supplier claim note generated from a delivery pre-check, with send shortcuts. */
export default function ClaimNoteBody({ precheckId }) {
  const [note, setNote] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    api.get(`/staff-app/receiving/prechecks/${precheckId}/claim`).then(({ data }) => {
      setNote(data); setSubject(data.subject); setBody(data.body);
    }).catch((err) => { toast.error(err.response?.data?.detail || 'Could not build the claim note'); setNote({ failed: true }); });
  }, [precheckId]);

  if (!note) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (note.failed) return <p className="py-6 text-sm text-muted-foreground text-center">Claim note unavailable.</p>;

  const full = `${subject}\n\n${body}`;
  const digits = (note.supplier.phone || '').replace(/[^\d]/g, '');
  const phone = digits.startsWith('0') ? `233${digits.slice(1)}` : digits;
  const copy = async () => {
    try { await navigator.clipboard.writeText(full); toast.success('Claim note copied'); } catch { toast.error('Copy failed — select the text and copy it'); }
  };
  const share = () => navigator.share({ title: subject, text: body }).catch(() => {});

  return (
    <div className="space-y-3" data-testid="claim-note">
      <p className="text-xs text-muted-foreground" data-testid="claim-note-supplier">
        To: <b className="text-foreground">{note.supplier.name || 'Supplier'}</b>{note.supplier.email ? ` · ${note.supplier.email}` : ''}{note.supplier.phone ? ` · ${note.supplier.phone}` : ''}
      </p>
      <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="text-sm font-medium" data-testid="claim-note-subject" />
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} className="text-sm leading-relaxed" data-testid="claim-note-body" />
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={copy} className="gap-1.5" data-testid="claim-note-copy"><Copy className="w-4 h-4" /> Copy</Button>
        <Button asChild variant="outline" className="gap-1.5" data-testid="claim-note-email">
          <a href={`mailto:${note.supplier.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}><Mail className="w-4 h-4" /> Email</a>
        </Button>
        <Button asChild variant="outline" className="gap-1.5" data-testid="claim-note-whatsapp">
          <a href={`https://wa.me/${phone}?text=${encodeURIComponent(full)}`} target="_blank" rel="noreferrer"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
        </Button>
        {typeof navigator !== 'undefined' && navigator.share && (
          <Button variant="outline" onClick={share} className="gap-1.5" data-testid="claim-note-share"><Share2 className="w-4 h-4" /> Share</Button>
        )}
      </div>
    </div>
  );
}
